import { useEffect, useState } from 'react';
import { create } from 'zustand';

/**
 * Portal themes: `terminal` (dark) and `functional` (light). Until the user
 * picks one, the technical console (integration, mappings, DLQ...) opens in
 * `terminal` and the business modules (Data Hub, Documentos) in
 * `functional`. The header switch then applies the choice to the whole
 * portal, remembered in this browser.
 */
export type ThemeName = 'terminal' | 'functional';
export type ThemePreference = 'light' | 'dark';

/** Route prefixes that belong to business-user modules. */
export const FUNCTIONAL_PREFIXES = ['/data-hub', '/documents'] as const;

export function isFunctionalPath(pathname: string): boolean {
  return FUNCTIONAL_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

const STORAGE_KEY = 'is.theme';
/** Earlier the switch only covered the business modules; a dark choice there still holds. */
const LEGACY_KEY = 'is.functionalTheme';

function readPreference(): ThemePreference | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
    return localStorage.getItem(LEGACY_KEY) === 'dark' ? 'dark' : null;
  } catch {
    return null;
  }
}

interface ThemeState {
  /** null until the user picks: each area keeps its default. */
  preference: ThemePreference | null;
  setPreference: (p: ThemePreference) => void;
}

export const useThemeStore = create<ThemeState>((set) => ({
  preference: readPreference(),
  setPreference: (p) => {
    try {
      localStorage.setItem(STORAGE_KEY, p);
    } catch {
      // Private mode or blocked storage: the choice lasts for this session only.
    }
    set({ preference: p });
  },
}));

export function themeFor(pathname: string, pref: ThemePreference | null): ThemeName {
  const scheme = pref ?? (isFunctionalPath(pathname) ? 'light' : 'dark');
  return scheme === 'light' ? 'functional' : 'terminal';
}

/** Applies the theme to <html> so dialogs and toasts in the top layer inherit it. */
export function useApplyTheme(pathname: string): ThemeName {
  const pref = useThemeStore((s) => s.preference);
  const theme = themeFor(pathname, pref);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    // Pages outside the app shell (login, legal) are always terminal.
    return () => {
      document.documentElement.dataset.theme = 'terminal';
    };
  }, [theme]);
  return theme;
}

/** Current theme name, re-rendering when <html data-theme> changes. */
export function useThemeName(): ThemeName {
  const read = (): ThemeName =>
    document.documentElement.dataset.theme === 'functional' ? 'functional' : 'terminal';
  const [name, setName] = useState<ThemeName>(read);
  useEffect(() => {
    const obs = new MutationObserver(() => setName(read()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  return name;
}

/** Reads an HSL-triplet token (e.g. `--chart-1`) as a CSS color string. */
export function tokenColor(name: string): string {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(`--${name}`).trim();
  return raw ? `hsl(${raw})` : 'currentColor';
}

export interface ChartTheme {
  series: string[];
  other: string;
  axis: string;
  grid: string;
  surface: string;
  foreground: string;
  muted: string;
  tooltipBg: string;
  tooltipBorder: string;
  status: { good: string; warning: string; serious: string; critical: string };
}

/** Chart colors for the active theme (recharts takes colors as props, not classes). */
export function useChartTheme(): ChartTheme {
  const theme = useThemeName();
  const [value, setValue] = useState<ChartTheme>(() => readChartTheme());
  useEffect(() => {
    setValue(readChartTheme());
  }, [theme]);
  return value;
}

function readChartTheme(): ChartTheme {
  return {
    series: [1, 2, 3, 4, 5, 6].map((i) => tokenColor(`chart-${i}`)),
    other: tokenColor('chart-other'),
    axis: tokenColor('chart-axis'),
    grid: tokenColor('chart-grid'),
    surface: tokenColor('chart-surface'),
    foreground: tokenColor('foreground'),
    muted: tokenColor('muted-foreground'),
    tooltipBg: tokenColor('popover'),
    tooltipBorder: tokenColor('border'),
    status: {
      good: tokenColor('success'),
      warning: tokenColor('warning'),
      serious: tokenColor('chart-3'),
      critical: tokenColor('danger'),
    },
  };
}
